import { useBillingUser } from '@common/billing/billing-page/use-billing-user';
import { useProducts } from '@common/billing/pricing-table/use-products';
import { AccountSettingsPanel } from '@common/auth/ui/account-settings/account-settings-panel';
import { AccountSettingsId } from '@common/auth/ui/account-settings/account-settings-sidenav';
import { Trans } from '@ui/i18n/trans';
import { useState, useMemo, useEffect } from 'react';
import { UpsellBillingCycle } from '@common/billing/pricing-table/find-best-price';
import { Button } from '@ui/buttons/button';
import { Link } from 'react-router';
import { CheckIcon } from '@ui/icons/material/Check';
import { FormattedPrice } from '@common/billing/formatted-price';
import { Product } from '@common/billing/product';
import { findBestPrice } from '@common/billing/pricing-table/find-best-price';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';
import { ProgressCircle } from '@ui/progress/progress-circle';
import { FormattedDate } from '@ui/i18n/formatted-date';
import { FormattedRelativeTime } from '@ui/i18n/formatted-relative-time';

export function SubscriptionPanel() {
    const { subscription } = useBillingUser();
    const query = useProducts();
    const [selectedCycle, setSelectedCycle] = useState<UpsellBillingCycle>('monthly');
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

    const products = useMemo(() => {
        return query.data?.products.filter(p => !p.hidden) || [];
    }, [query.data?.products]);

    // Auto-select current plan or the first plan
    useEffect(() => {
        if (products.length && !selectedProduct) {
            const current = products.find(p => p.id === subscription?.product_id);
            setSelectedProduct(current || products[0]);
        }
    }, [products, subscription, selectedProduct]);

    const savingsPercentage = useMemo(() => {
        if (!products.length) return 0;
        const decreases = products.map(product => {
            const monthly = findBestPrice('monthly', product.prices);
            const yearly = findBestPrice('yearly', product.prices);
            if (!monthly || !yearly) return 0;
            const monthlyAmount = monthly.amount * 12;
            const yearlyAmount = yearly.amount;
            return Math.round(((monthlyAmount - yearlyAmount) / monthlyAmount) * 100);
        });
        return Math.max(...decreases, 0);
    }, [products]);

    const selectedPrice = useMemo(() => {
        if (!selectedProduct) return null;
        return findBestPrice(selectedCycle, selectedProduct.prices);
    }, [selectedProduct, selectedCycle]);

    const checkoutUrl = useMemo(() => {
        if (!selectedProduct || !selectedPrice) return null;
        if (subscription?.product_id === selectedProduct.id && subscription?.price_id === selectedPrice.id) return null;

        if (subscription) {
            return `/billing/change-plan/${selectedProduct.id}/${selectedPrice.id}/confirm`;
        }
        return `/checkout/${selectedProduct.id}/${selectedPrice.id}`;
    }, [selectedProduct, selectedPrice, subscription]);

    const buttonLabel = useMemo(() => {
        if (!subscription || !subscription.product_id) {
            return <Trans message="Assinar" />;
        }

        if (selectedProduct?.id === subscription.product_id) {
            return <Trans message="Plano Atual" />;
        }

        const currentIndex = products.findIndex(p => p.id === subscription.product_id);
        const selectedIndex = products.findIndex(p => p.id === selectedProduct?.id);

        if (selectedIndex > currentIndex) {
            return <Trans message="Fazer Upgrade" />;
        }

        if (selectedIndex < currentIndex) {
            return <Trans message="Fazer Dowgrade" />;
        }

        return <Trans message="Assinar" />;
    }, [subscription, selectedProduct, products]);

    return (
        <AccountSettingsPanel
            id={AccountSettingsId.Subscription}
            title={<Trans message="Subscription" />}
        >
            <div className="mb-40 space-y-32">
                <div className="flex flex-col items-center gap-24 text-center">
                    <div>
                        <h2 className="text-2xl font-bold text-white mb-8 tracking-tight">
                            <Trans message="Choose your plan" />
                        </h2>
                        <div className="text-sm text-white/50 max-w-md mx-auto leading-relaxed">
                            <Trans message="Enjoy unlimited access. Cancel or switch plans anytime." />
                        </div>
                    </div>

                    {subscription?.product && (
                        <CurrentPlanCard subscription={subscription} />
                    )}

                    {/* Premium Toggle */}
                    <div className="relative flex flex-row items-center p-4 bg-white/[0.03] rounded border border-white/5 backdrop-blur-md min-w-[240px] h-48 select-none">
                        <button
                            onClick={() => setSelectedCycle('monthly')}
                            className={clsx(
                                "relative z-10 flex-1 h-full flex items-center justify-center text-sm font-bold transition-colors duration-200",
                                selectedCycle === 'monthly' ? "text-black" : "text-white/40 hover:text-white/70"
                            )}
                        >
                            {selectedCycle === 'monthly' && (
                                <motion.div
                                    layoutId="cycle-indicator"
                                    className="absolute inset-0 bg-primary rounded shadow-[0_0_20px_rgba(var(--be-primary),0.3)]"
                                    transition={{ type: "spring", bounce: 0.15, duration: 0.5 }}
                                />
                            )}
                            <span className="relative z-20"><Trans message="Mensal" /></span>
                        </button>
                        <button
                            onClick={() => setSelectedCycle('yearly')}
                            className={clsx(
                                "relative z-10 flex-1 h-full flex items-center justify-center gap-6 text-sm font-bold transition-colors duration-200",
                                selectedCycle === 'yearly' ? "text-black" : "text-white/40 hover:text-white/70"
                            )}
                        >
                            {selectedCycle === 'yearly' && (
                                <motion.div
                                    layoutId="cycle-indicator"
                                    className="absolute inset-0 bg-primary rounded shadow-[0_0_20px_rgba(var(--be-primary),0.3)]"
                                    transition={{ type: "spring", bounce: 0.15, duration: 0.5 }}
                                />
                            )}
                            <div className="relative z-20 flex items-center gap-6">
                                <Trans message="Anual" />
                                {savingsPercentage > 0 && (
                                    <span className={clsx(
                                        "text-[9px] px-6 py-1 rounded bg-positive/10 text-positive border border-positive/20 whitespace-nowrap",
                                        selectedCycle === 'yearly' && "bg-black/10 text-black border-black/10"
                                    )}>
                                        <Trans message="Save :percentage%" values={{ percentage: savingsPercentage }} />
                                    </span>
                                )}
                            </div>
                        </button>
                    </div>
                </div>

                {query.isLoading ? (
                    <div className="flex justify-center py-64">
                        <ProgressCircle isIndeterminate className="text-primary" />
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-16">
                        {products.map(product => {
                            const isSelected = selectedProduct?.id === product.id;
                            const isCurrent = subscription?.product_id === product.id || (!subscription && product.free);
                            return (
                                <PlanCard
                                    key={product.id}
                                    product={product}
                                    selectedCycle={selectedCycle}
                                    isCurrent={isCurrent}
                                    isSelected={isSelected}
                                    onSelect={() => setSelectedProduct(product)}
                                />
                            );
                        })}
                    </div>
                )}

                <div className="pt-32 flex flex-col items-center gap-16">
                    <Button
                        variant="flat"
                        color="primary"
                        size="lg"
                        className="w-full max-w-sm font-black text-lg py-16 transition-all hover:scale-[1.02] active:scale-[0.98] shadow-[0_10px_40px_rgba(var(--be-primary),0.2)] disabled:opacity-50"
                        elementType={checkoutUrl ? Link : undefined}
                        to={checkoutUrl || undefined}
                        disabled={!checkoutUrl}
                    >
                        {buttonLabel}
                    </Button>

                    <div className="text-[10px] text-white/20 font-medium tracking-tight flex items-center gap-6 select-none">
                        <span>•</span>
                        <Trans message="Secured by 256-bit SSL encryption" />
                        <span>•</span>
                        <Trans message="Payments processed by Stripe" />
                        <span>•</span>
                    </div>
                </div>
            </div>
        </AccountSettingsPanel>
    );
}

interface PlanCardProps {
    product: Product;
    selectedCycle: UpsellBillingCycle;
    isCurrent: boolean;
    isSelected: boolean;
    onSelect: () => void;
}

function PlanCard({ product, selectedCycle, isCurrent, isSelected, onSelect }: PlanCardProps) {
    const price = findBestPrice(selectedCycle, product.prices);

    return (
        <div
            onClick={onSelect}
            className={clsx(
                "group relative overflow-hidden rounded border transition-all duration-300 cursor-pointer",
                isSelected
                    ? "border-primary bg-primary/[0.04] shadow-[0_4px_12px_rgba(0,0,0,0.1)] ring-1 ring-primary/20"
                    : "border-white/5 bg-white/[0.02] hover:bg-white/[0.04]"
            )}
        >
            {product.recommended && (
                <div className="absolute top-0 right-10">
                    <div className="bg-primary px-8 py-3 text-[8px] font-black uppercase tracking-widest text-black rounded-b">
                        <Trans message="Most Popular" />
                    </div>
                </div>
            )}

            <div className="p-20 md:p-28 flex flex-col md:flex-row md:items-center gap-20 md:gap-32">
                <div className="flex-auto select-none">
                    <div className="flex items-center flex-wrap gap-12 mb-6">
                        <h3 className={clsx(
                            "text-xl font-bold tracking-tight transition-colors",
                            isSelected ? "text-primary px-1" : "text-white"
                        )}>
                            {product.name}
                        </h3>
                        {isCurrent && (
                            <div className="flex items-center gap-4 px-8 py-2 rounded bg-primary/10 text-primary text-[9px] font-bold uppercase tracking-wider">
                                <CheckIcon size="xs" />
                                <Trans message="Ativo" />
                            </div>
                        )}
                    </div>
                    <p className="text-xs text-white/40 max-w-sm leading-relaxed mb-12">
                        {product.description}
                    </p>

                    <div className="flex flex-wrap gap-x-16 gap-y-6">
                        {product.feature_list.map(feature => (
                            <div key={feature} className="flex items-center gap-6 text-[11px] font-medium text-white/60">
                                <CheckIcon className={clsx("w-14 h-14", isSelected ? "text-primary" : "text-white/20")} />
                                <span><Trans message={feature} /></span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="flex flex-col items-start md:items-end gap-12 min-w-[140px] md:border-l md:border-white/5 md:pl-32">
                    <div className="text-left md:text-right">
                        {price ? (
                            <div className="flex flex-col">
                                <FormattedPrice
                                    price={price}
                                    priceClassName="text-3xl font-black text-white tracking-tighter"
                                    periodClassName="text-xs text-white/40 font-medium ml-4"
                                />
                            </div>
                        ) : (
                            <span
                                className="text-3xl font-black text-primary/90 tracking-tighter uppercase italic py-2"
                                style={{ background: 'none', backgroundColor: 'transparent', backgroundImage: 'none', boxShadow: 'none', border: 'none' }}
                            >
                                <Trans message="GRATUITO" />
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {isSelected && (
                <div className="absolute inset-y-0 left-0 w-1 bg-primary shadow-[2px_0_10px_rgba(var(--be-primary),0.4)]" />
            )}
        </div>
    );
}

function CurrentPlanCard({ subscription }: { subscription: any }) {
    const { user } = useUser('me');
    const isFree = !subscription || !subscription.product_id;

    return (
        <div className="w-full max-w-lg mx-auto p-20 rounded border border-primary/20 bg-primary/[0.03] backdrop-blur-sm relative overflow-hidden group hover:bg-primary/[0.05] transition-colors duration-500">
            <div className="absolute top-0 right-0 p-12 opacity-[0.03] group-hover:opacity-10 transition-opacity duration-700">
                <CheckIcon size="xl" />
            </div>
            <div className="flex flex-col gap-12 relative z-10 text-left">
                <div className="flex items-center justify-between">
                    <span className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-6">
                        <div className="w-6 h-6 rounded-full bg-primary" />
                        <Trans message="Assinatura Ativa" />
                    </span>
                    <span className="text-[10px] font-bold text-white/30 uppercase tracking-tight">
                        <Trans message="Membro desde" /> <FormattedDate date={subscription?.created_at || user?.created_at} />
                    </span>
                </div>

                <div className="flex items-center gap-12">
                    <h3 className="text-2xl font-black text-white leading-none tracking-tight">
                        {isFree ? <Trans message="Impidium Free" /> : subscription.product?.name}
                    </h3>
                    {!isFree && (
                        <div className="px-8 py-2 rounded bg-primary text-black text-[9px] font-black uppercase leading-none">
                            <Trans message="Premium" />
                        </div>
                    )}
                </div>

                <div className="pt-16 border-t border-white/5 flex flex-col md:flex-row md:items-center justify-between gap-12">
                    <div className="text-[11px] text-white/50 flex items-center gap-6">
                        <Trans message="Status do faturamento:" />
                        <span className="text-white font-bold bg-white/5 px-6 py-1 rounded">
                            {isFree ? <Trans message="N/A" /> : <Trans message="Habilitado" />}
                        </span>
                    </div>

                    {!isFree && subscription.renews_at && (
                        <div className="text-[11px] text-white/50 flex items-center gap-4">
                            <Trans message="Próxima renovação:" />
                            <span className="text-primary font-bold">
                                <FormattedDate date={subscription.renews_at} />
                            </span>
                        </div>
                    )}

                    {isFree && (
                        <div className="text-[11px] text-white/40 italic">
                            <Trans message="Acesso gratuito vitalício" />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
